import { filter } from 'lodash';
import { createContext, useEffect, useState } from 'react';
// material
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Button,
  Grid,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
// components
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead } from '../../sections/user';
import { CampaignListToolbar, CampaignMoreMenu } from '../../sections/campaign';
import CreateAudienceModal from '../../components/campaign/CreateAudienceModal';
import CampaignCards from '../../components/campaign/CampaignCards';
import instance from '../../api/axiosRequest';

// ----------------------------------------------------------------------

export const AudienceModalContext = createContext();

const TABLE_HEAD = [
  { id: 'id', label: 'ID', alignRight: false },
  { id: 'campain', label: 'Campaign Name', alignRight: false },
  { id: 'from', label: 'From', alignRight: false },
  { id: 'template', label: 'Template Name', alignRight: false },
  { id: 'createdAt', label: 'Created At', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => _user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function Campaigns() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  useEffect(() => {
    const getCampaigns = async () => {
      setLoading(true);
      try {
        const { data } = await instance.get('/vendor/emailCampain');
        setRows(data.data.rows);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.log(error);
      }
    };
    localStorage.removeItem("campaign");
    getCampaigns();
  }, [reload]);

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const filteredCampaigns = applySortFilter(rows, getComparator(order, orderBy), filterName);

  const isUserNotFound = filteredCampaigns.length === 0;

  return (
    <AudienceModalContext.Provider value={{ open, setOpen, setSelected }}>
      <Page title="User">
        <Container>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
            <Typography variant="h4" gutterBottom>
              Email Campaigns
            </Typography>
            <Button onClick={() => navigate('create')} variant="contained">
              Create Campaign
            </Button>
          </Stack>
          <Grid container spacing={4} mb={4}>
            <Grid item xs={12} md={3}>
              <CampaignCards title="Total contacts" details="5365" />
            </Grid>
            <Grid item xs={12} md={3}>
              <CampaignCards title="Opens" details="535" />
            </Grid>
            <Grid item xs={12} md={3}>
              <CampaignCards title="Clicks" details="365" />
            </Grid>
            <Grid item xs={12} md={3}>
              <CampaignCards title="Blocklists" details="53" />
            </Grid>
          </Grid>
          <Typography variant="h5" mb={1}>
            Previous Campaigns
          </Typography>
          <Card>
            <CampaignListToolbar
              numSelected={selected.length}
              filterName={filterName}
              onFilterName={handleFilterByName}
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={rows.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                  />
                  <TableBody>
                    {filteredCampaigns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                      const { id, campain, from, template, createdAt } = row;
                      const isItemSelected = selected.indexOf(campain) !== -1;

                      return (
                        <TableRow
                          hover
                          onClick={() => {
                            localStorage.setItem('campaign', JSON.stringify(row));
                            navigate('/dashboard/campaigns/campaigns/details');
                          }}
                          key={id}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={isItemSelected} onChange={(event) => handleClick(event, campain)} />
                          </TableCell>
                          <TableCell component="th" scope="row" padding="none">
                            {id}
                          </TableCell>
                          <TableCell align="left">{campain}</TableCell>
                          <TableCell align="left">{from}</TableCell>
                          <TableCell align="left">{template}</TableCell>
                          <TableCell align="left">{new Date(createdAt).toLocaleString()}</TableCell>
                          <TableCell align="right">
                            <CampaignMoreMenu row={row} setReload={setReload} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>

                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          {loading ? <CircularProgress /> : <SearchNotFound searchQuery={filterName} />}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Card>
          <CreateAudienceModal />
        </Container>
      </Page>
    </AudienceModalContext.Provider>
  );
}
